import React, { useEffect, useMemo, useCallback } from "react";
import { useState } from "react";
import { classNames } from "../../../../../helpers/classNames";
import { BiPalette } from "react-icons/bi";
import ButtonDropdown from "../../../../ButtonDropdown";
import SmartTableConditionalFormattingRow from "./SmartTable___ConditionalFormatting___Row";
import { ApiRequest, API_AUTH_TYPES, API_ROUTES } from "../../../../../api";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import areArraysIdentical from "../../../../../helpers/arrayArraysIdentical";

const SmartTableConditionalFormatting = ({ columns, dbConditionalFormatting, viewId, tableId, onConditionalFormattingChange }) => {
    const [conditionalFormattingRules, setConditionalFormattingRules] = useState(dbConditionalFormatting || []);
    const [changesMade, setChangesMade] = useState(false);
    const [open, setOpen] = useState(false);
    const usersApi = new ApiRequest(API_ROUTES.USERS, API_AUTH_TYPES.ADVISOR);
    const { data: advisors, isLoading: isLoadingAdvisors } = useQuery({
        queryFn: usersApi.getFn({ endpoint: 'index_advisors' }),
        queryKey: 'advisors'
    });

    useEffect(() => {
        setChangesMade(!areArraysIdentical(conditionalFormattingRules, dbConditionalFormatting));
    }, [conditionalFormattingRules, dbConditionalFormatting]);

    useEffect(() => {
        setConditionalFormattingRules(dbConditionalFormatting || []);
    }, [dbConditionalFormatting]);

    const viewsApi = new ApiRequest(
        API_ROUTES.SMART_TABLE_VIEWS,
        API_AUTH_TYPES.ADVISOR
    );

    const queryClient = useQueryClient();

    const saveConditionalFormatting = async (rules) => {
        return await viewsApi.patch({
            endpoint: "update_view_conditional_formatting",
            body: {
                view_id: viewId,
                conditional_formatting: rules,
            },
        });
    };

    const { mutate: runSaveConditionalFormatting, isLoading: isSaving } = useMutation(saveConditionalFormatting, {
        onSuccess: (data) => {
            console.log('Conditional formatting saved successfully:', data);
            if (onConditionalFormattingChange) {
                onConditionalFormattingChange(conditionalFormattingRules);
            }

            // Invalidate the query for the smart table data
            queryClient.invalidateQueries(["smart-table-complete-data", { tableId }]);

        },
        onError: (error) => {
            console.error("Error saving conditional formatting:", error);
            alert(`Error saving conditional formatting: ${error.message || 'Unknown error'}`);
        }
    });

    // Local change handler - doesn't save, just updates local state
    const handleLocalConditionalFormattingChange = useCallback((newRules) => {
        setConditionalFormattingRules(newRules);
    }, []);

    // Save handler - only called when Apply button is clicked
    const handleApplyConditionalFormattingClick = useCallback(() => {
        console.log('Applying conditional formatting rules:', conditionalFormattingRules);
        console.log('View ID:', viewId);
        runSaveConditionalFormatting(conditionalFormattingRules);
    }, [conditionalFormattingRules, runSaveConditionalFormatting, viewId]);

    const moveRule = useCallback((dragIndex, hoverIndex) => {
        const newRules = [...(conditionalFormattingRules || [])];
        const draggedRule = newRules[dragIndex];
        newRules.splice(dragIndex, 1);
        newRules.splice(hoverIndex, 0, draggedRule);
        handleLocalConditionalFormattingChange(newRules);
    }, [conditionalFormattingRules, handleLocalConditionalFormattingChange]);

    const handleAddCondition = () => {
        if (!columns || columns.length === 0) return;

        // Find the first formattable column (number, text, date, etc.)
        const firstFormattableColumn = columns.find(col =>
            !["button", "copy_to_clipboard"].includes(col.col_type)
        );
        if (!firstFormattableColumn) return;

        // Set default operator based on column type
        let defaultOperator = "is not empty";
        if (firstFormattableColumn.col_type === "number") {
            defaultOperator = ">";
        } else if (firstFormattableColumn.col_type === "date") {
            defaultOperator = "before";
        } else if (firstFormattableColumn.col_type === "single_select") {
            defaultOperator = "is";
        }

        const newRule = {
            col_id: firstFormattableColumn.col_id,
            col_type: firstFormattableColumn.col_type,
            operator: defaultOperator,
            comparison: null,
            background_color: "#3B82F6", // Default blue color
            text_color: "#FFFFFF", // Default white text
        };

        const newRulesArray = [...(conditionalFormattingRules || []), newRule];
        handleLocalConditionalFormattingChange(newRulesArray);
    };

    if (!columns) {
        return (
            <button className="rounded text-gray-400 px-2 py-1 flex space-x-1 items-center" disabled>
                <BiPalette className="h-6 w-6" />
                <p className="hidden xl:flex">Format</p>
            </button>
        );
    }

    return (
        <div className="">
            <ButtonDropdown open={open} setOpen={setOpen} position="top-10 left-0 -translate-x-1/2">
                <div className="p-2 min-w-[800px]">
                    <div className="mb-2">
                        <h3 className="text-base font-medium text-gray-900 mb-1">Conditional Formatting</h3>
                        <p className="text-sm text-gray-500">Set background and text colors based on cell values • Drag to reorder rules</p>
                    </div>

                    <DndProvider backend={HTML5Backend}>
                        <div className="space-y-1">
                            {(conditionalFormattingRules || []).map((rule, index) => (
                                <SmartTableConditionalFormattingRow
                                    key={`${rule.col_id}-${index}`}
                                    allRules={conditionalFormattingRules || []}
                                    onRulesChange={handleLocalConditionalFormattingChange}
                                    columns={columns}
                                    rule={rule}
                                    thisRuleIndex={index}
                                    moveRule={moveRule}
                                    advisors={advisors}
                                />
                            ))}
                        </div>
                    </DndProvider>

                    {conditionalFormattingRules.length === 0 && (
                        <div className="text-center py-6 text-gray-500">
                            <p className="text-lg mb-2">No formatting rules</p>
                            <p className="text-sm">Click + Add Formatting Rule to start styling your data</p>
                        </div>
                    )}

                    <div className="flex justify-between items-center mt-3 pt-2 border-t border-gray-200">
                        <button
                            onClick={handleAddCondition}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={isSaving}
                        >
                            + Add Formatting Rule
                        </button>

                        <button
                            onClick={handleApplyConditionalFormattingClick}
                            disabled={isSaving || !changesMade}
                            className={classNames(
                                'inline-flex items-center px-4 py-2 text-sm font-medium border rounded-md transition-colors duration-150',
                                isSaving ? 'bg-gray-200 text-gray-500 cursor-not-allowed border-gray-200' :
                                    changesMade ? 'bg-rose-600 hover:bg-rose-700 text-white border-rose-600 animate-pulse' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
                            )}
                        >
                            {isSaving ? 'Saving...' : (changesMade ? 'Apply Changes' : 'Applied')}
                        </button>
                    </div>
                </div>
            </ButtonDropdown>

            <button
                onClick={() => setOpen(true)}
                className={classNames(
                    "rounded hover:ring-2 hover:ring-gray-300 text-gray-600 px-2 py-1 flex space-x-1 items-center",
                    (conditionalFormattingRules && conditionalFormattingRules.length > 0) && "bg-rose-100",
                    changesMade && "ring-2 ring-rose-400 ring-offset-1"
                )}
            >
                <BiPalette className="h-6 w-6" />
                <p className="hidden xl:flex">
                    {(conditionalFormattingRules && conditionalFormattingRules.length > 0)
                        ? `${conditionalFormattingRules.length} rule${conditionalFormattingRules.length > 1 ? "s" : ""}`
                        : "Format"}
                    {changesMade && !isSaving && ' *'}
                </p>
            </button>
        </div>
    );
};

export default SmartTableConditionalFormatting; 